import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Plus, Edit2, Trash2, ExternalLink, LogOut, Users, Vault } from "lucide-react";

const CATEGORIES = [
  "Life Insurance",
  "Health Insurance",
  "Investments",
  "Bank Accounts",
  "Wills & Legal Documents",
  "Emergency Contacts",
  "Other"
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [isEditItemOpen, setIsEditItemOpen] = useState(false);
  const [isAddDependentOpen, setIsAddDependentOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category: "",
    title: "",
    details: "",
    link: "",
    dependents: []
  });
  const [dependentForm, setDependentForm] = useState({
    dependent_name: "",
    dependent_email: ""
  });
  // UI state for searchable multi-select of dependents on an item
  const [depSearchAdd, setDepSearchAdd] = useState("");
  const [depSearchEdit, setDepSearchEdit] = useState("");

  const addDependentToItemAdd = (dep) => {
    const idStr = String(dep.id);
    if ((itemForm.dependents || []).map(String).includes(idStr)) return;
    setItemForm({ ...itemForm, dependents: [...(itemForm.dependents || []), idStr] });
    setDepSearchAdd("");
  };

  const addDependentToItemEdit = (dep) => {
    const idStr = String(dep.id);
    if ((itemForm.dependents || []).map(String).includes(idStr)) return;
    setItemForm({ ...itemForm, dependents: [...(itemForm.dependents || []), idStr] });
    setDepSearchEdit("");
  };

  const removeDependentFromItem = (id) => {
    const idStr = String(id);
    setItemForm({ ...itemForm, dependents: (itemForm.dependents || []).filter(d => String(d) !== idStr) });
  };

  const getFilteredDependents = (search) => dependents
    .filter(d => !((itemForm.dependents || []).map(String).includes(String(d.id))))
    .filter(d => `${d.dependent_name} ${d.dependent_email}`.toLowerCase().includes(String(search || "").toLowerCase()));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, vaultRes, depRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/vault/items`),
        axios.get(`${API}/dependents`)
      ]);
      setUser(userRes.data);
      setVaultItems(vaultRes.data);
      setDependents(depRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('remembral_token');
    localStorage.removeItem('remembral_user');
    navigate('/');
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...itemForm, type:" add", dependents: (itemForm.dependents || [])};
      await axios.post(`${API}/vault/items`, payload);
      toast.success('Item added successfully');
      setIsAddItemOpen(false);
      setDepSearchAdd("");
      setItemForm({ category: "", title: "", details: "", link: "", dependents: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...itemForm, aman: "aman", dependents: (itemForm.dependents || [])};
      await axios.put(`${API}/vault/items/${editingItem.id}`, payload);
      console.log("SUCCESS DEBUG PUT Add Dependents", itemForm)
      toast.success('Item updated successfully');
      setIsEditItemOpen(false);
      setDepSearchEdit("");
      setEditingItem(null);
      setItemForm({ category: "", title: "", details: "", link: "", dependents: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/vault/items/${itemId}`);
      toast.success('Item deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setItemForm({
      category: item.category,
      title: item.title,
      details: item.details,
      link: item.link || "",
      // normalize dependent ids to strings for the UI multi-select
      dependents: (item.dependents || []).map(d => String(d?.id ?? d?.dependent_id ?? d))
    });
    setDepSearchEdit("");
    setIsEditItemOpen(true);
  };

  const handleAddDependent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dependents`, dependentForm);
      toast.success('Dependent added successfully');
      setIsAddDependentOpen(false);
      setDependentForm({ dependent_name: "", dependent_email: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dependent');
    }
  };

  const handleDeleteDependent = async (depId) => {
    if (!window.confirm('Are you sure you want to remove this dependent?')) return;
    
    try {
      await axios.delete(`${API}/dependents/${depId}`);
      toast.success('Dependent removed successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to remove dependent');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const groupedItems = CATEGORIES.reduce((acc, category) => {
    acc[category] = vaultItems.filter(item => item.category === category);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Remembral</h1>
                <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/access-portal')}
                data-testid="access-portal-button"
              >
                <Users className="w-4 h-4 mr-2" />
                Access Portal
              </Button>
              <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

      </header>

      {/* Main Content */}
      
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="vault" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="vault" data-testid="tab-vault">
              <Vault className="w-4 h-4 mr-2" />
              My Vault
            </TabsTrigger>
            <TabsTrigger value="dependents" data-testid="tab-dependents">
              <Users className="w-4 h-4 mr-2" />
              Dependents
            </TabsTrigger>
          </TabsList>

          {/* Vault Tab */}
          <TabsContent value="vault" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Your Secure Vault</h2>
                <p className="text-slate-600">Store and manage all your critical information</p>
              </div>
              <Dialog open={isAddItemOpen} onOpenChange={(open) => { setIsAddItemOpen(open); if (!open) setDepSearchAdd(""); if (open) setItemForm({ category: "", title: "", details: "", link: "", dependents: [] }); }}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-item-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-item-dialog">
                  <DialogHeader>
                    <DialogTitle>Add Vault Item</DialogTitle>
                    <DialogDescription>Add important information to your secure vault</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddItem} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={itemForm.category}
                        onValueChange={(value) => setItemForm({ ...itemForm, category: value })}
                        required
                      >
                        <SelectTrigger data-testid="select-category">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat} data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={itemForm.title}
                        onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                        placeholder="e.g., Life Insurance Policy"
                        required
                        data-testid="input-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="details">Details</Label>
                      <Textarea
                        id="details"
                        value={itemForm.details}
                        onChange={(e) => setItemForm({ ...itemForm, details: e.target.value })}
                        placeholder="Policy number, coverage amount, contact information..."
                        rows={4}
                        required
                        data-testid="input-details"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="link">Link (Optional)</Label>
                      <Input
                        id="link"
                        value={itemForm.link}
                        onChange={(e) => setItemForm({ ...itemForm, link: e.target.value })}
                        placeholder="https://..."
                        type="url"
                        data-testid="input-link"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item_dependents">Share with Dependents (optional)</Label>
                      <div>
                        <input
                          id="item_dependents"
                          value={depSearchAdd}
                          onChange={(e) => setDepSearchAdd(e.target.value)}
                          placeholder={dependents.length ? "Search dependents to add..." : "No dependents available"}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          data-testid="search-dependents"
                        />
                        {depSearchAdd && getFilteredDependents(depSearchAdd).length > 0 && (
                          <div className="mt-1 max-h-60 overflow-auto border rounded bg-white z-10">
                            {getFilteredDependents(depSearchAdd).map((dep) => (
                              <div
                                key={dep.id}
                                onClick={() => addDependentToItemAdd(dep)}
                                className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                                data-testid={`search-result-dep-${dep.id}`}
                              >
                                <div className="text-sm">
                                  <div className="font-medium">{dep.dependent_name}</div>
                                  <div className="text-xs text-slate-500">{dep.dependent_email}</div>
                                </div>
                                <Plus className="w-4 h-4 text-indigo-600" />
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-2">
                          {(itemForm.dependents || []).map((id) => {
                            const dep = dependents.find(d => String(d.id) === String(id));
                            if (!dep) return null;
                            return (
                              <div key={id} className="inline-flex items-center gap-2 px-2 py-1 bg-slate-100 rounded text-sm">
                                <span>{dep.dependent_name}</span>
                                <button type="button" onClick={() => removeDependentFromItem(id)} className="text-red-500">×</button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-add-item">
                      Add Item
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Vault Items by Category */}
            <div className="space-y-6">
              {CATEGORIES.map((category) => (
                groupedItems[category].length > 0 && (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3" data-testid={`category-header-${category.toLowerCase().replace(/\s+/g, '-')}`}>{category}</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {groupedItems[category].map((item) => (
                        <Card key={item.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`vault-item-${item.id}`}>
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <CardTitle className="text-lg">{item.title}</CardTitle>
                                <CardDescription className="text-xs mt-1">{item.category}</CardDescription>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(item)}
                                  data-testid={`edit-item-${item.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-slate-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteItem(item.id)}
                                  data-testid={`delete-item-${item.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{item.details}</p>
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
                                data-testid={`item-link-${item.id}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                                View Link
                              </a>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )
              ))}

              {vaultItems.length === 0 && (
                <Card className="border-dashed border-2 border-slate-300" data-testid="empty-vault">
                  <CardContent className="py-12 text-center">
                    <Vault className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No items yet</h3>
                    <p className="text-slate-600 mb-4">Start securing your information by adding your first item</p>
                    <Button
                      onClick={() => setIsAddItemOpen(true)}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="empty-vault-add-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Item
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Dependents Tab */}
          <TabsContent value="dependents" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Your Dependents</h2>
                <p className="text-slate-600">Manage who can access your vault information</p>
              </div>
              <Dialog open={isAddDependentOpen} onOpenChange={setIsAddDependentOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-dependent-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Dependent
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-dependent-dialog">
                  <DialogHeader>
                    <DialogTitle>Add Dependent</DialogTitle>
                    <DialogDescription>Add a trusted individual who can access your vault</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddDependent} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="dependent_name">Name</Label>
                      <Input
                        id="dependent_name"
                        value={dependentForm.dependent_name}
                        onChange={(e) => setDependentForm({ ...dependentForm, dependent_name: e.target.value })}
                        placeholder="John Doe"
                        required
                        data-testid="input-dependent-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dependent_email">Email</Label>
                      <Input
                        id="dependent_email"
                        type="email"
                        value={dependentForm.dependent_email}
                        onChange={(e) => setDependentForm({ ...dependentForm, dependent_email: e.target.value })}
                        placeholder="john@example.com"
                        required
                        data-testid="input-dependent-email"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-add-dependent">
                      Add Dependent
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {dependents.map((dep) => (
                <Card key={dep.id} className="border-slate-200" data-testid={`dependent-${dep.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-slate-900">{dep.dependent_name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{dep.dependent_email}</p>
                        <span className="inline-block mt-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          Active
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDependent(dep.id)}
                        data-testid={`remove-dependent-${dep.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {dependents.length === 0 && (
              <Card className="border-dashed border-2 border-slate-300" data-testid="empty-dependents">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No dependents added</h3>
                  <p className="text-slate-600 mb-4">Add trusted individuals who can access your vault</p>
                  <Button
                    onClick={() => setIsAddDependentOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="empty-dependents-add-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Dependent
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Item Dialog */}
      <Dialog open={isEditItemOpen} onOpenChange={(open) => { setIsEditItemOpen(open); if (!open) setDepSearchEdit(""); if (!open) setEditingItem(null); }}>
        <DialogContent data-testid="edit-item-dialog">
          <DialogHeader>
            <DialogTitle>Edit Vault Item</DialogTitle>
            <DialogDescription>Update your vault item information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditItem} className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={itemForm.category}
                onValueChange={(value) => setItemForm({ ...itemForm, category: value })}
                required
              >
                <SelectTrigger data-testid="edit-select-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={itemForm.title}
                onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                placeholder="e.g., Life Insurance Policy"
                required
                data-testid="edit-input-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-details">Details</Label>
              <Textarea
                id="edit-details"
                value={itemForm.details}
                onChange={(e) => setItemForm({ ...itemForm, details: e.target.value })}
                placeholder="Policy number, coverage amount, contact information..."
                rows={4}
                required
                data-testid="edit-input-details"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-link">Link (Optional)</Label>
              <Input
                id="edit-link"
                value={itemForm.link}
                onChange={(e) => setItemForm({ ...itemForm, link: e.target.value })}
                placeholder="https://..."
                type="url"
                data-testid="edit-input-link"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-item-dependents">Share with Dependents (optional)</Label>
              <div>
                <input
                  id="edit-item-dependents"
                  value={depSearchEdit}
                  onChange={(e) => setDepSearchEdit(e.target.value)}
                  placeholder={dependents.length ? "Search dependents to add..." : "No dependents available"}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  data-testid="edit-search-dependents"
                />
                {depSearchEdit && getFilteredDependents(depSearchEdit).length > 0 && (
                  <div className="mt-1 max-h-60 overflow-auto border rounded bg-white z-10">
                    {getFilteredDependents(depSearchEdit).map((dep) => (
                      <div
                        key={dep.id}
                        onClick={() => addDependentToItemEdit(dep)}
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                        data-testid={`edit-search-result-dep-${dep.id}`}
                      >
                        <div className="text-sm">
                          <div className="font-medium">{dep.dependent_name}</div>
                          <div className="text-xs text-slate-500">{dep.dependent_email}</div>
                        </div>
                        <Plus className="w-4 h-4 text-indigo-600" />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-2">
                  {(itemForm.dependents || []).map((id) => {
                    const dep = dependents.find(d => String(d.id) === String(id));
                    if (!dep) return null;
                    return (
                      <div key={id} className="inline-flex items-center gap-2 px-2 py-1 bg-slate-100 rounded text-sm">
                        <span>{dep.dependent_name}</span>
                        <button type="button" onClick={() => removeDependentFromItem(id)} className="text-red-500">×</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-edit-item">
              Update Item
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}